import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Locales } from "@simplyk/common";

import Step1ImageEn from "./assets/tour-step1-en.svg";
import Step1ImageFr from "./assets/tour-step1-fr.svg";
import Step2ImageEn from "./assets/tour-step2-en.svg";
import Step2ImageFr from "./assets/tour-step2-fr.svg";
import Step3ImageEn from "./assets/tour-step3-en.svg";
import Step3ImageFr from "./assets/tour-step3-fr.svg";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardDialog } from "@/dashboard-design-system/Dialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

export type CheckInManagerTourDialogProps = {
  open: boolean;
  onClose?: () => void;
  step: 1 | 2 | 3;
  onGoToStep?: (step: 1 | 2 | 3) => void;
  onComplete?: () => void;
};

export const CheckInManagerTourDialog = ({
  open,
  onClose,
  step,
  onGoToStep,
  onComplete,
}: CheckInManagerTourDialogProps) => {
  return (
    <DashboardDialog open={open} onClose={onClose} maxWidth="sm" fullWidth hideIcon data-test="checkin-manager-tour">
      {step === 1 && <CheckInManagerTourDialogStep1Content onNextClick={() => onGoToStep?.(2)} />}
      {step === 2 && (
        <CheckInManagerTourDialogStep2Content onNextClick={() => onGoToStep?.(3)} onBackClick={() => onGoToStep?.(1)} />
      )}
      {step === 3 && (
        <CheckInManagerTourDialogStep3Content onBackClick={() => onGoToStep?.(2)} onComplete={onComplete} />
      )}
    </DashboardDialog>
  );
};

type CheckInManagerTourDialogStep1ContentProps = {
  onNextClick?: () => void;
};

const CheckInManagerTourDialogStep1Content = ({ onNextClick }: CheckInManagerTourDialogStep1ContentProps) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();

  return (
    <CheckInManagerTourDialogLayout
      slots={{
        // eslint-disable-next-line zeffy-linter/no-locale-condition
        image: locale === Locales.FR ? <Step1ImageFr /> : <Step1ImageEn />,
        step: t("dashboard", "checkInManager.tour.step.label", { step: 1, totalSteps: 3 }),
        title: t("dashboard", "checkInManager.tour.step1.title"),
        actions: (
          <DashboardButton variant="filled" onClick={onNextClick}>
            {t("dashboard", "checkInManager.tour.actions.next")}
          </DashboardButton>
        ),
      }}
    />
  );
};

type CheckInManagerTourDialogStep2ContentProps = {
  onNextClick?: () => void;
  onBackClick?: () => void;
};

const CheckInManagerTourDialogStep2Content = ({
  onNextClick,
  onBackClick,
}: CheckInManagerTourDialogStep2ContentProps) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();

  return (
    <CheckInManagerTourDialogLayout
      slots={{
        // eslint-disable-next-line zeffy-linter/no-locale-condition
        image: locale === Locales.FR ? <Step2ImageFr /> : <Step2ImageEn />,
        step: t("dashboard", "checkInManager.tour.step.label", { step: 2, totalSteps: 3 }),
        title: t("dashboard", "checkInManager.tour.step2.title"),
        actions: (
          <>
            <DashboardButton variant="filled" onClick={onNextClick}>
              {t("dashboard", "checkInManager.tour.actions.next")}
            </DashboardButton>
            <DashboardButton variant="outlined" onClick={onBackClick}>
              {t("dashboard", "checkInManager.tour.actions.back")}
            </DashboardButton>
          </>
        ),
      }}
    />
  );
};

type CheckInManagerTourDialogStep3ContentProps = {
  onBackClick?: () => void;
  onComplete?: () => void;
};

const CheckInManagerTourDialogStep3Content = ({
  onBackClick,
  onComplete,
}: CheckInManagerTourDialogStep3ContentProps) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();

  return (
    <CheckInManagerTourDialogLayout
      slots={{
        // eslint-disable-next-line zeffy-linter/no-locale-condition
        image: locale === Locales.FR ? <Step3ImageFr /> : <Step3ImageEn />,
        step: t("dashboard", "checkInManager.tour.step.label", { step: 3, totalSteps: 3 }),
        title: t("dashboard", "checkInManager.tour.step3.title"),
        actions: (
          <>
            <DashboardButton variant="filled" onClick={onComplete}>
              {t("dashboard", "checkInManager.tour.actions.complete")}
            </DashboardButton>
            <DashboardButton variant="outlined" onClick={onBackClick}>
              {t("dashboard", "checkInManager.tour.actions.back")}
            </DashboardButton>
          </>
        ),
      }}
    />
  );
};

type CheckInManagerTourDialogLayoutProps = {
  slots: {
    image: React.ReactNode;
    step: React.ReactNode;
    title: React.ReactNode;
    actions: React.ReactNode;
  };
};

const CheckInManagerTourDialogLayout = ({ slots }: CheckInManagerTourDialogLayoutProps) => {
  return (
    <Stack gap={2}>
      <Stack gap={3} alignItems="center">
        <Box width={{ xs: "70%", sm: 312 }} sx={{ "& > *": { width: "100%", height: "100%" } }}>
          {slots.image}
        </Box>
        <Stack alignItems="center">
          <DashboardTypography
            variant="body2"
            vibe="text-neutral-quiet"
            textAlign="center"
            sx={{ textTransform: "uppercase" }}
          >
            {slots.step}
          </DashboardTypography>
          <DashboardTypography variant="h4" vibe="text-neutral-intense" textAlign="center">
            {slots.title}
          </DashboardTypography>
        </Stack>
      </Stack>
      <Stack direction="row-reverse" alignItems="center" justifyContent="flex-start" gap={1}>
        {slots.actions}
      </Stack>
    </Stack>
  );
};
